import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { parse, format, isValid } from 'date-fns';
import { FaTimes } from 'react-icons/fa';

const StockEntry = ({ produtoSelecionado = null, fecharModal = null, isModal = false }) => {
    const [produtos, setProdutos] = useState([]);
    const [filteredProdutos, setFilteredProdutos] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [estoqueAtual, setEstoqueAtual] = useState(0);
    const [produtoInfo, setProdutoInfo] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [mensagemSucesso, setMensagemSucesso] = useState('');
    const [campoValidade, setCampoValidade] = useState('');
    const [validadeError, setValidadeError] = useState('');
    const [precoFormatado, setPrecoFormatado] = useState('');
    const [pesoFormatado, setPesoFormatado] = useState('');
    const [quantidadeFormatada, setQuantidadeFormatada] = useState('');
    
    const modalRef = useRef(null); // ← NOVO

    const [entrada, setEntrada] = useState({
        produto_id: '',
        codigo_barras: '',
        nome_produto: '',
        numero_lote: '',
        preco_custo: '',
        validade: '',
        quantidade: '',
        peso_embalagem: '',
    });

    useEffect(() => {
        api.get('/produtos')
            .then(response => setProdutos(response.data))
            .catch(error => console.error("Erro ao buscar produtos:", error));
    }, []);

    useEffect(() => {
        if (produtoSelecionado) {
            setEntrada(prev => ({
                ...prev,
                produto_id: produtoSelecionado.id,
                nome_produto: produtoSelecionado.nome,
                codigo_barras: produtoSelecionado.codigo_barras
            }));
            setProdutoInfo(produtoSelecionado);
            setEstoqueAtual(produtoSelecionado.estoque_atual || 0);
            setShowDropdown(false);
            setPrecoFormatado('');
            setPesoFormatado('');
            setQuantidadeFormatada('');
            if (produtoSelecionado.validade) {
                const formatada = format(new Date(produtoSelecionado.validade), 'dd/MM/yy');
                setCampoValidade(formatada);
            }
        }
    }, [produtoSelecionado]);

    // ← NOVO: Fechar modal ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                if (fecharModal) fecharModal();
            }
        };

        if (isModal) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isModal, fecharModal]);

    const handleNomeChange = (e) => {
        const nomeDigitado = e.target.value;
        setEntrada({ ...entrada, nome_produto: nomeDigitado });

        if (nomeDigitado.length > 0) {
            const produtosFiltrados = produtos.filter(p =>
                p.nome.toLowerCase().includes(nomeDigitado.toLowerCase())
            );
            setFilteredProdutos(produtosFiltrados);
            setShowDropdown(true);
        } else {
            setShowDropdown(false);
            resetCamposProduto();
        }
    };

    const handleCodigoBarrasChange = (e) => {
        const codigoDigitado = e.target.value;
        setEntrada({ ...entrada, codigo_barras: codigoDigitado });

        if (codigoDigitado.length > 0) {
            const produtosFiltrados = produtos.filter(p =>
                p.codigo_barras.includes(codigoDigitado) ||
                (p.codigo_barras_alternativo && p.codigo_barras_alternativo.includes(codigoDigitado))
            );
            setFilteredProdutos(produtosFiltrados);
            setShowDropdown(true);
        } else {
            setShowDropdown(false);
            resetCamposProduto();
        }
    };

    const handleProdutoSelect = (produto) => {
        setEntrada({
            ...entrada,
            produto_id: produto.id,
            nome_produto: produto.nome,
            codigo_barras: produto.codigo_barras,
        });
        setProdutoInfo(produto);
        setEstoqueAtual(produto.estoque_atual || 0);
        setShowDropdown(false);
    };

    const resetCamposProduto = () => {
        setEntrada({
            ...entrada,
            produto_id: '',
            nome_produto: '',
            codigo_barras: '',
        });
        setProdutoInfo(null);
        setEstoqueAtual(0);
    };

    const handleChange = (e) => {
        setEntrada({ ...entrada, [e.target.name]: e.target.value });
    };

    const handleValidadeChange = (e) => {
        const raw = e.target.value.replace(/\D/g, '');
        let formatado = '';

        if (raw.length <= 2) {
            formatado = raw;
        } else if (raw.length <= 4) {
            formatado = `${raw.slice(0, 2)}/${raw.slice(2)}`;
        } else {
            formatado = `${raw.slice(0, 2)}/${raw.slice(2, 4)}/${raw.slice(4)}`;
        }

        setCampoValidade(formatado);

        if (raw.length === 6) {
            const tentativa = `${raw.slice(0, 2)}/${raw.slice(2, 4)}/20${raw.slice(4)}`;
            const parsed = parse(tentativa, 'dd/MM/yyyy', new Date());
            if (isValid(parsed)) {
                const hoje = new Date();
                hoje.setHours(0, 0, 0, 0);
                if (parsed < hoje) {
                    setValidadeError('A data de validade não pode ser anterior à data de hoje.');
                    setEntrada(prev => ({ ...prev, validade: '' }));
                } else {
                    setValidadeError('');
                    setEntrada(prev => ({
                        ...prev,
                        validade: format(parsed, 'yyyy-MM-dd'),
                    }));
                }
            } else {
                setValidadeError('Data inválida.');
                setEntrada(prev => ({ ...prev, validade: '' }));
            }
        } else {
            setValidadeError('');
        }
    };

    const handlePrecoChange = (e) => {
        const raw = e.target.value.replace(/\D/g, '');
        const centavos = raw.padStart(3, '0');
        const reais = centavos.slice(0, -2);
        const cent = centavos.slice(-2);
        const formatado = `${Number(reais).toLocaleString('pt-BR')},${cent}`;
        setPrecoFormatado(formatado);
        const valorFinal = parseFloat(`${reais}.${cent}`);
        setEntrada(prev => ({ ...prev, preco_custo: valorFinal }));
    };

    // ← CORRIGIDO: Máscara automática para peso (igual preço)
    const handlePesoChange = (e) => {
        const raw = e.target.value.replace(/\D/g, '');
        
        if (!raw) {
            setPesoFormatado('');
            setEntrada(prev => ({ ...prev, peso_embalagem: '' }));
            return;
        }
        
        // Adiciona zeros à esquerda para garantir pelo menos 4 dígitos (1 inteiro + 3 decimais)
        const milhar = raw.padStart(4, '0');
        const inteiro = milhar.slice(0, -3);
        const decimal = milhar.slice(-3);
        
        // Formata com vírgula
        const formatado = `${Number(inteiro).toLocaleString('pt-BR')},${decimal}`;
        setPesoFormatado(formatado);
        
        // Converte para número (com ponto decimal)
        const valorFinal = parseFloat(`${inteiro}.${decimal}`);
        setEntrada(prev => ({ ...prev, peso_embalagem: valorFinal }));
        
        console.log('DEBUG PESO:', {
            digitado: e.target.value,
            raw: raw,
            formatado: formatado,
            valorFinal: valorFinal
        });
    };

    // ← Máscara para quantidade (inteiro)
    const handleQuantidadeChange = (e) => {
        const raw = e.target.value.replace(/\D/g, '');
        setQuantidadeFormatada(raw);
        setEntrada(prev => ({ 
            ...prev, 
            quantidade: raw ? parseInt(raw) : '' 
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!entrada.produto_id) {
            setErrorMessage('Selecione um produto válido antes de registrar a entrada.');
            return;
        }

        if (!entrada.peso_embalagem || parseFloat(entrada.peso_embalagem) <= 0) {
            setErrorMessage('Informe o peso da embalagem deste lote.');
            return;
        }

        if (!entrada.validade) {
            setErrorMessage('Informe uma data de validade válida (não pode ser anterior a hoje).');
            return;
        }

        console.log('📤 Dados sendo enviados:', {
            ...entrada,
            peso_embalagem: parseFloat(entrada.peso_embalagem),
            quantidade: parseInt(entrada.quantidade)
        });

        try {
            await api.post('/movimentacoes', { 
                ...entrada, 
                tipo: 'entrada',
                peso_embalagem: parseFloat(entrada.peso_embalagem),
                quantidade: parseInt(entrada.quantidade)
            });
            
            setMensagemSucesso('Entrada registrada com sucesso!');
            setEntrada({
                produto_id: '',
                codigo_barras: '',
                nome_produto: '',
                numero_lote: '',
                preco_custo: '',
                validade: '',
                quantidade: '',
                peso_embalagem: '',
            });
            setCampoValidade('');
            setValidadeError('');
            setPrecoFormatado('');
            setPesoFormatado('');
            setQuantidadeFormatada('');
            setEstoqueAtual(0);
            setProdutoInfo(null);
            setErrorMessage('');

            setTimeout(() => {
                setMensagemSucesso('');
                if (fecharModal) fecharModal();
            }, 2000);
        } catch (error) {
            console.error('❌ Erro completo:', error.response?.data);
            if (error.response && error.response.data.erro) {
                setErrorMessage(error.response.data.erro);
            } else {
                setErrorMessage('Erro desconhecido ao registrar entrada.');
            }
        }
    };

    const formatarUnidade = (unidade) => {
        const unidades = {
            'kg': 'kg',
            'g': 'g',
            'l': 'l',
            'ml': 'ml',
            'unidade': 'un',
            'caixa': 'cx'
        };
        return unidades[unidade?.toLowerCase()] || unidade;
    };

    // Renderizar conteúdo do formulário
    const renderFormulario = () => (
        <>
            {mensagemSucesso && (
                <div className="mb-3 p-2 bg-green-100 text-green-700 border border-green-400 rounded text-sm">
                    {mensagemSucesso}
                </div>
            )}

            {errorMessage && (
                <div className="mb-3 p-2 bg-red-100 text-red-700 border border-red-400 rounded text-sm">
                    {errorMessage}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3">
                <div className="relative">
                    <label className="block text-sm text-gray-700 font-medium mb-1">
                        Nome do Produto
                    </label>
                    <input
                        type="text"
                        name="nome_produto"
                        value={entrada.nome_produto}
                        autoComplete="off"
                        onChange={handleNomeChange}
                        placeholder="Digite o nome do produto..."
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                        disabled={!!produtoSelecionado}
                    />
                    {showDropdown && filteredProdutos.length > 0 && (
                        <ul className="absolute w-full border rounded-lg bg-white shadow-md mt-1 z-20 max-h-32 overflow-y-auto">
                            {filteredProdutos.map(produto => (
                                <li
                                    key={produto.id}
                                    className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm"
                                    onClick={() => handleProdutoSelect(produto)}
                                >
                                    <span className="block truncate">{produto.nome}</span>
                                    <span className="text-xs text-gray-500">{produto.codigo_barras}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div>
                    <label className="block text-sm text-gray-700 font-medium mb-1">
                        Código de Barras
                    </label>
                    <input
                        type="text"
                        name="codigo_barras"
                        value={entrada.codigo_barras}
                        autoComplete="off"
                        onChange={handleCodigoBarrasChange}
                        placeholder="Digite o código de barras..."
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                        disabled={!!produtoSelecionado}
                    />
                </div>

                <div>
                    <label className="block text-sm text-gray-700 font-medium mb-1">
                        Número do Lote
                    </label>
                    <input
                        type="text"
                        name="numero_lote"
                        value={entrada.numero_lote}
                        autoComplete="off"
                        onChange={handleChange}
                        placeholder="Digite o número do lote..."
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                    />
                </div>

                <div>
                    <label className="block text-sm text-gray-700 font-medium mb-1">
                        Peso da Embalagem *
                        {produtoInfo && (
                            <span className="text-xs text-gray-500 ml-1">
                                (em {formatarUnidade(produtoInfo.unidade_peso)})
                            </span>
                        )}
                    </label>
                    <input
                        type="text"
                        name="peso_embalagem"
                        value={pesoFormatado}
                        onChange={handlePesoChange}
                        placeholder="0,000"
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                    />
                </div>

                <div>
                    <label className="block text-sm text-gray-700 font-medium mb-1">
                        Preço de Custo (R$)
                    </label>
                    <input
                        type="text"
                        name="preco_custo"
                        value={precoFormatado}
                        onChange={handlePrecoChange}
                        placeholder="0,00"
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                    />
                </div>

                <div>
                    <label className="block text-sm text-gray-700 font-medium mb-1">
                        Data de Validade
                    </label>
                    <input
                        type="text"
                        name="validade"
                        value={campoValidade}
                        onChange={handleValidadeChange}
                        placeholder="DD/MM/AA"
                        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 ${validadeError ? 'border-red-400 focus:ring-red-400' : 'focus:ring-blue-500'}`}
                        required
                    />
                    {validadeError && (
                        <p className="mt-1 text-xs text-red-600">{validadeError}</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm text-gray-700 font-medium mb-1">
                        Quantidade
                    </label>
                    <input
                        type="text"
                        name="quantidade"
                        value={quantidadeFormatada}
                        autoComplete="off"
                        onChange={handleQuantidadeChange}
                        placeholder="Digite a quantidade..."
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                    />
                </div>

                <button 
                    type="submit"
                    className="w-full bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 transition-colors text-sm font-medium mt-4"
                >
                    Registrar Entrada
                </button>
            </form>
        </>
    );

    // MODO MODAL
    if (isModal) {
        return (
            <div 
                className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4"
                style={{ zIndex: 9999 }}
            >
                <div 
                    ref={modalRef}
                    className="bg-white rounded-lg shadow-lg w-full max-w-md relative"
                    style={{ 
                        maxHeight: '95vh',
                        overflowY: 'auto'
                    }}
                >
                    <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-700">
                            Registrar Entrada de Estoque
                        </h2>
                        <button
                            onClick={fecharModal}
                            className="text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full p-2 transition-colors"
                            title="Fechar"
                        >
                            <FaTimes className="w-5 h-5" />
                        </button>                    
                    </div>

                    <div className="p-4">
                        {renderFormulario()}
                    </div>
                </div>
            </div>
        );
    }

    // MODO PÁGINA COMPLETA
    return (
        <div className="max-w-2xl mx-auto mt-4 bg-white p-6 md:p-8 rounded-lg shadow-lg">
            <h2 className="text-xl md:text-2xl font-semibold text-center text-gray-700 mb-4 md:mb-6">
                Registrar Entrada de Estoque
            </h2>
            {renderFormulario()}
        </div>
    );
};

export default StockEntry;